import { LoadingSmall } from "@/components/LoadingSmall";
import { getPlayerHealth } from "@/sideEffects/api";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { CheckCircleIcon, MinusCircleIcon, XCircleIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { selectActiveFirm } from "@/store/appSelectors";
import { useSelector } from "react-redux";
import { PlayerHealth } from "@/lib/types";
import { motion } from "framer-motion";

interface Props {
  playerId: number;
}

// Animation variants
const containerVariants = {
  initial: {
    opacity: 0,
    scale: 0.8,
    y: 5,
  },
  animate: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 15,
      when: "beforeChildren",
      staggerChildren: 0.1,
    },
  },
  hover: {
    scale: 1.05,
    transition: {
      type: "spring",
      stiffness: 400,
      damping: 10,
    },
  },
};

const iconVariants = {
  initial: {
    opacity: 0,
    rotate: -30,
    scale: 0,
  },
  animate: {
    opacity: 1,
    rotate: 0,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 15,
    },
  },
};

const textVariants = {
  initial: { opacity: 0, x: -5 },
  animate: {
    opacity: 1,
    x: 0,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 20,
      delay: 0.1,
    },
  },
};

export const DashboardPlayerHealthStatus = ({ playerId }: Props) => {
  const { t } = useTranslation();
  const activeFirm = useSelector(selectActiveFirm);
  const { data: playerHealth, isLoading } = useQuery({
    queryKey: ["getPlayerHealth", playerId],
    queryFn: () => getPlayerHealth(Number(playerId), String(activeFirm?.firmGuid)),
    refetchInterval: 10 * 1000,
  });

  const health = playerHealth?.data.health;

  if (isLoading) {
    return <LoadingSmall />;
  }

  if (health === null || health === undefined) {
    return "-";
  }

  // Define status styles based on health value
  const getStatusStyles = () => {
    if (!health) null;

    const healthStatus = health;

    switch (healthStatus) {
      case PlayerHealth.ok:
        return {
          icon: <CheckCircleIcon className="w-4 h-4 text-green-500" />,
          textColor: "text-green-600",
          bgColor: "bg-green-50 dark:bg-green-950/20",
        };
      case PlayerHealth.unhealthy:
        return {
          icon: <XCircleIcon className="w-4 h-4 text-red-500" />,
          textColor: "text-red-600",
          bgColor: "bg-red-50 dark:bg-red-950/20",
        };
      default:
        return {
          icon: <MinusCircleIcon className="w-4 h-4 text-gray-500" />,
          textColor: "text-gray-600",
          bgColor: "bg-gray-50 dark:bg-gray-950/20",
        };
    }
  };

  const { icon, textColor, bgColor } = getStatusStyles();

  return (
    <motion.div
      className={cn("flex items-center gap-1.5 px-2 py-1 rounded-full w-fit", bgColor)}
      variants={containerVariants}
      initial="initial"
      animate="animate"
      whileHover="hover"
    >
      <motion.div variants={iconVariants}>{icon}</motion.div>
      <motion.span className={cn("text-xs font-medium", textColor)} variants={textVariants}>
        {t(`playerInfo.healthEnum.${health}`)}
      </motion.span>
    </motion.div>
  );
};
